import {useEffect, useState} from 'react';
import styles from './styles.module.css';
import http from 'services/http';
import {useHistory} from 'react-router';
import Swal from 'sweetalert2';
import Modal from 'react-modal';
import {alerta} from '../../../components/Alert';
import dayjs from 'dayjs';

const customStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        width: '400px',
        maxHeight: '90vh',
        overflowY: 'auto',
        background: '#f6ecc7',
    },
};

const formatarDataHora = (valor) => (valor ? dayjs(valor).format('DD/MM/YY HH:mm') : '--');

const formatarValor = (valor) => Number(valor ?? 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

/**
 * "Aguardando número" é o estado dos sorteios de 4 e 5 dígitos que já encerraram:
 * o vencedor só sai depois que o administrador lança o número, pelo modal.
 */
const descreverStatus = (sorteio) => {
    if (!sorteio.ativo) return {texto: 'Cancelado', cor: '#ffc107'};
    if (!sorteio.status && sorteio.sem_ganhador) return {texto: 'Sem ganhador', cor: '#6c757d'};
    if (!sorteio.status) return {texto: 'Finalizado', cor: '#1B970C'};
    if (sorteio.aguardando_numero) return {texto: 'Aguardando número', cor: '#0d6efd'};
    if (sorteio.esgotado) return {texto: 'Esgotado — venda travada', cor: '#d33'};
    return {texto: 'Pendente', cor: '#6c757d'};
};

export default function Sorteio() {
    const [sorteios, setSorteio] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalIsOpen, setIsOpen] = useState(false);
    const [sorteioSelecionado, setSorteioSelecionado] = useState(null);
    const [numero, setNumero] = useState('');
    const [lancando, setLancando] = useState(false);

    const history = useHistory();

    function abrirModal(sorteio) {
        setSorteioSelecionado(sorteio);
        setNumero('');
        setIsOpen(true);
    }

    function fecharModal() {
        setIsOpen(false);
        setSorteioSelecionado(null);
    }

    async function requestSorteios() {
        try {
            setLoading(true);
            const response = await http.get('/sorteios');
            setSorteio(response.data.data);
        } catch (e) {
            alerta(e.response?.data ?? {success: false, message: 'Erro ao listar sorteios.'});
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        requestSorteios();
    }, [])

    async function handleDelete(id) {
        Swal.fire({
            title: 'Deseja cancelar o sorteio?',
            text: '',
            type: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            reverseButtons: true,
            confirmButtonText: 'Sim, cancelar!',
            cancelButtonText: 'Não, voltar'
        }).then((result) => {
            if (result.value) {
                deleteSorteio(id);
            }
        });
    }

    async function deleteSorteio(id) {
        try {
            const res = await http.patch('/sorteios/' + id);
            requestSorteios();
            alerta(res.data)
        } catch (error) {
            alerta(error.response?.data ?? {success: false, message: 'Erro ao cancelar sorteio.'});
        }
    }

    /**
     * Cada bilhete participante recebeu um número na venda. Lançado o número, o
     * sorteio encerra: com o bilhete que tem aquele número como ganhador, ou sem
     * ganhador se ninguém o tem. Erro (número com dígitos errados, prazo) mantém
     * o modal aberto para corrigir.
     */
    async function lancarNumero() {
        const digitos = sorteioSelecionado.tipo === '5_digitos' ? 5 : 4;

        if (numero.length !== digitos) {
            alerta({success: false, message: `O número deste sorteio precisa ter ${digitos} dígitos!`});
            return;
        }

        setLancando(true);

        try {
            const res = await http.post(`/sorteios/${sorteioSelecionado.id}/numero`, {numero});
            alerta(res.data);
            await requestSorteios();
            fecharModal();
        } catch (error) {
            alerta(error.response?.data ?? {success: false, message: 'Erro ao lançar o número.'});
            await requestSorteios();
        } finally {
            setLancando(false);
        }
    }

    const ganhador = sorteioSelecionado?.aposta_ganhadora;
    const digitosEsperados = sorteioSelecionado?.tipo === '5_digitos' ? 5 : 4;

    return (
        <>
            <div className="titulo-principal">Sorteio</div>
            <div className={`conteudo-principal ${styles.header_sorteio}`}>
                <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => history.push('/sorteio/criar')}
                >
                    Novo Sorteio
                </button>
            </div>
            <Modal
                isOpen={modalIsOpen}
                onRequestClose={fecharModal}
                style={customStyles}
                contentLabel="Detalhes do sorteio"
            >
                {sorteioSelecionado && (
                    <div className={styles.containerModal}>
                        <div className={styles.premio}>
                            <i className={`fa fa-5x ${sorteioSelecionado.premio_icon ?? ''}`} aria-hidden="true"></i>
                        </div>

                        {ganhador && (
                            <div className={styles.ganhador}>
                                <p>Ganhador:</p>
                                <strong>{ganhador.codigo}</strong>
                            </div>
                        )}

                        <div className={styles.info}>
                            <div className={styles.text_info}>
                                <p>Tipo:</p>
                                <strong>{sorteioSelecionado.tipo_label}</strong>
                            </div>
                            <div className={styles.text_info}>
                                <p>Prêmio:</p>
                                <strong>
                                    {sorteioSelecionado.premio}
                                    {sorteioSelecionado.valor_premio
                                        ? ` — R$ ${formatarValor(sorteioSelecionado.valor_premio)}`
                                        : ''}
                                </strong>
                            </div>
                            <div className={styles.text_info}>
                                <p>Encerramento:</p>
                                <strong>{formatarDataHora(sorteioSelecionado.data_sorteio)}</strong>
                            </div>
                            <div className={styles.text_info}>
                                <p>Modalidades:</p>
                                <strong>{(sorteioSelecionado.modalidades_nomes ?? []).join(', ') || 'Todas'}</strong>
                            </div>
                            <div className={styles.text_info}>
                                <p>Gerentes:</p>
                                <strong>{(sorteioSelecionado.gerentes ?? []).join(', ') || 'Todos'}</strong>
                            </div>
                            {sorteioSelecionado.numeros_total != null && (
                                <div className={styles.text_info}>
                                    <p>Bilhetes com número:</p>
                                    <strong>
                                        {Number(sorteioSelecionado.numeros_distribuidos ?? 0).toLocaleString('pt-BR')}
                                        {' de '}
                                        {Number(sorteioSelecionado.numeros_total).toLocaleString('pt-BR')}
                                    </strong>
                                </div>
                            )}
                            {sorteioSelecionado.esgotado && (
                                <p className={styles.aviso_esgotado}>
                                    Os números acabaram e a venda das modalidades deste sorteio está
                                    travada. Lance o número, depois do encerramento, ou cancele o sorteio.
                                </p>
                            )}
                            {sorteioSelecionado.numero_sorteado && (
                                <div className={styles.text_info}>
                                    <p>Número lançado:</p>
                                    <strong>
                                        {sorteioSelecionado.numero_sorteado}
                                        {sorteioSelecionado.sem_ganhador ? ' — sem ganhador' : ''}
                                    </strong>
                                </div>
                            )}
                            {sorteioSelecionado.info && (
                                <div className={styles.text_info}>
                                    <p>Informações:</p>
                                    <strong>{sorteioSelecionado.info}</strong>
                                </div>
                            )}

                            {ganhador && (
                                <>
                                    <div className={styles.text_info}>
                                        <p>Gerente:</p>
                                        <strong>{ganhador.gerente}</strong>
                                    </div>
                                    <div className={styles.text_info}>
                                        <p>Cambista:</p>
                                        <strong>{ganhador.colaborador}</strong>
                                    </div>
                                    <div className={styles.text_info}>
                                        <p>Data:</p>
                                        <strong>{ganhador.data}</strong>
                                    </div>
                                </>
                            )}
                        </div>

                        {sorteioSelecionado.aguardando_numero && (
                            <div className={styles.lancamento}>
                                <label htmlFor="numero-sorteado">
                                    Número vencedor ({digitosEsperados} dígitos):
                                </label>
                                <input
                                    id="numero-sorteado"
                                    type="text"
                                    inputMode="numeric"
                                    className="form-control"
                                    placeholder={'0'.repeat(digitosEsperados)}
                                    maxLength={digitosEsperados}
                                    value={numero}
                                    onChange={(e) => setNumero(e.target.value.replace(/\D/g, ''))}
                                />
                                <button
                                    type="button"
                                    className="btn btn-success btn-block m-t"
                                    disabled={lancando}
                                    onClick={lancarNumero}
                                >
                                    {lancando ? 'Lançando...' : 'Lançar número'}
                                </button>
                            </div>
                        )}

                        <div className={styles.buttonClose}>
                            <button className="btn btn-info" onClick={fecharModal}>
                                Fechar
                            </button>
                        </div>
                    </div>
                )}
            </Modal>

            {loading && <div className="loader"/>}

            {(sorteios.length > 0) ? (
                <div className={styles.table_sorteio}>
                    <table className="table">
                        <thead>
                        <tr>
                            <th style={{padding: '5px'}}>ID</th>
                            <th style={{padding: '5px'}}>Criação</th>
                            <th style={{padding: '5px'}}>Encerramento</th>
                            <th style={{padding: '5px'}}>Prêmio</th>
                            <th style={{padding: '5px'}}>Status</th>
                            <th style={{padding: '5px'}}>Ver</th>
                            <th style={{padding: '5px'}}>Cancelar</th>
                        </tr>
                        </thead>
                        <tbody>
                        {sorteios.map((item) => {
                            const status = descreverStatus(item);

                            return (
                                <tr key={item.id}>
                                    <td>{item.id}</td>
                                    <td>{formatarDataHora(item.created_at)}</td>
                                    <td>{formatarDataHora(item.data_sorteio)}</td>
                                    <td>
                                        {item.premio}
                                        {item.valor_premio ? ` — R$ ${formatarValor(item.valor_premio)}` : ''}
                                    </td>
                                    <td style={{color: status.cor}}>{status.texto}</td>
                                    <td>
                                        <button
                                            className="btn btn-link"
                                            onClick={() => abrirModal(item)}
                                        >
                                            Visualizar
                                        </button>
                                    </td>
                                    <td>
                                        {(item.ativo && item.status) ? (
                                            <button
                                                className="btn btn-link"
                                                onClick={() => handleDelete(item.id)}
                                            >
                                                cancelar
                                            </button>
                                        ) : '--'}
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            ) : (
                !loading && (
                    <div className={styles.noResult}>
                        <p>
                            Nenhum sorteio encontrado
                        </p>
                    </div>
                )
            )
            }
        </>
    );
}
